import { useEffect } from 'react';
import { QueryHookOptions, QueryResult, useQuery } from '@apollo/client';
import getUpcomingSessionsSearchCriteria from '@tools/getUpcomingSessionsSearchCriteria';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { useAppReducer } from '@components/app/AppReducer';
import useAppStats from '@hooks/useAppStats';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import { ME } from '@graphql/operations/user/me';
import { MeQuery, MeQueryVariables } from '@graphql/gql/graphql';
import { appVariables } from '../apollo/appVariables';

const LOG = LoggerFactory.create('useMeQuery');

type MeQueryOptions = QueryHookOptions<MeQuery, MeQueryVariables>;

/**
 * Convenience hook for calling the ME query
 * Ensures we use consistent variables
 */
function useMeQuery(queryOptions?: Omit<MeQueryOptions, 'variables' | 'skip'>): QueryResult<MeQuery> {
  // If a page indicates that it is a public page (i.e. no auth needed) via pageProps, this query is skipped
  const { publicPage } = appVariables();
  const appStats = useAppStats();
  const {
    state: { initialLocale }
  } = useAppReducer();
  const locale = useLocaleForQueries() || initialLocale;
  const sessionSearchCriteria = getUpcomingSessionsSearchCriteria(appStats?.firstLoadTime);

  const options: MeQueryOptions = {
    ...queryOptions,
    variables: {
      locale,
      platform: 'web',
      sessionCriteria: {
        ...sessionSearchCriteria,
        locale
      }
    },
    skip: locale == null || appStats?.firstLoadTime == null || publicPage
  };

  const { data, error, loading, ...rest } = useQuery(ME, options);

  useEffect(() => {
    if (error) {
      LOG.error('Error fetching user event data', error);
    }
  }, [error]);

  return { data, error, loading, ...rest };
}

export default useMeQuery;
